import { SovereignConfig, SovereignManifest, SovereignGroup, SyncDiagnostic, SyncPhaseName, SyncPhaseResult, SyncRunResult } from '../types';
import { IStorage } from '../interfaces/IStorage';
import { IRemoteAdapter } from '../interfaces/IRemoteAdapter';
import { Logger } from '../utils/Logger';
import { SovereignS3nc } from '../SovereignS3nc';
import { PATHS, DEFAULTS } from '../utils/Constants';
import { SyncError, NetworkError } from '../utils/Errors';
import { env } from '../utils/Environment';
import { ManifestFetchResult } from './ManifestManager';

export interface SyncOrchestratorContext {
    config: SovereignConfig;
    storage: IStorage;
    getRemote: () => IRemoteAdapter | undefined;
    getPublicRemote: () => IRemoteAdapter | undefined;
    getGlobalRemote: () => IRemoteAdapter | undefined;
    isSyncing: () => boolean;
    setSyncing: (value: boolean) => void;
    syncWorker?: any;
    
    // Core methods to delegate back or use from other managers
    syncBlacklist: () => Promise<void>;
    syncAdminKey: () => Promise<void>;
    ensureGlobalRegistration: () => Promise<void>;
    updateFollowingPublicKeys: () => Promise<void>;
    discoverUsers: () => Promise<any>;
    autoFollowUsers: (users: any) => Promise<void>;
    processModerationRequests: () => Promise<void>;
    syncManifest: () => Promise<void>;
    generateManifest: () => Promise<SovereignManifest>;
    fetchManifest: (userId: string) => Promise<SovereignManifest | null>;
    fetchManifestWithMeta?: (userId: string, options?: { forceRefresh?: boolean; ttlMs?: number }) => Promise<ManifestFetchResult>;
    resolveFullManifest?: (userId: string, rootManifest: SovereignManifest) => Promise<SovereignManifest>;
    resolveSubManifest?: (userId: string, partitionKey: string, ref: any) => Promise<any>;
    syncGroups: (today: string) => Promise<void>;
    
    encrypt: (data: Uint8Array, key: string) => Promise<Uint8Array>;
    decrypt: (data: Uint8Array, key: string) => Promise<Uint8Array>;
    calculateHashedContent: (data: Uint8Array, key?: string) => string;
    handleConflict: (path: string, localData: Uint8Array, remoteData: Uint8Array) => Promise<'local' | 'remote' | 'abort' | { mergedData: Uint8Array }>;
    createRemote: (userId: string) => IRemoteAdapter;
    getModulePath: (moduleName: string, subPath: string, type: 'private' | 'public' | 'followed') => string;
    onModuleUpdate: (moduleName: string, path: string) => Promise<void> | void;
    registeredModules: any[];
    getModuleInstances?: () => any[];
    flushDatabases?: () => Promise<void>;
    closeDatabases?: () => Promise<void>;
    /** Emit a sync progress event. Stage describes the current phase; total/done are optional file counts. */
    emitSyncProgress: (stage: string, done?: number, total?: number, runId?: string, state?: 'running' | 'succeeded' | 'failed', phase?: SyncPhaseName) => void;
    emitSyncDiagnostic: (diagnostic: SyncDiagnostic) => void;
    emitSyncResult: (result: SyncRunResult) => void;
}

export class SyncOrchestrator {
    constructor(private ctx: SyncOrchestratorContext) {}

    async sync(forceSync: boolean = false): Promise<SyncRunResult> {
        const startedAt = Date.now();
        const runId = `${startedAt}-${env.generateId(12)}`;
        const phases: SyncPhaseResult[] = [];
        const diagnostics: SyncDiagnostic[] = [];
        const legacyStages: Partial<Record<SyncPhaseName, string>> = {
            own_data: 'syncing_own_data', registration: 'registering', discovery: 'discovering_users',
            followed_data: 'syncing_followed', blobs: 'syncing_blobs'
        };
        const finish = (): SyncRunResult => {
            const failed = phases.filter(p => p.status === 'failed').length;
            const succeeded = phases.filter(p => p.status === 'succeeded').length;
            const status = failed === 0 ? (succeeded === 0 ? 'skipped' : 'succeeded') : (succeeded === 0 ? 'failed' : 'partial');
            const result = { runId, status, startedAt, completedAt: Date.now(), phases, diagnostics } as SyncRunResult;
            this.ctx.emitSyncResult(result);
            return result;
        };
        const phase = async (name: SyncPhaseName, operation: () => Promise<void>, total?: number): Promise<boolean> => {
            const phaseStartedAt = Date.now();
            const stage = legacyStages[name] || name;
            this.ctx.emitSyncProgress(stage, total === undefined ? undefined : 0, total, runId, 'running', name);
            try {
                await operation();
                phases.push({ name, status: 'succeeded', startedAt: phaseStartedAt, completedAt: Date.now() });
                this.ctx.emitSyncProgress(stage, total, total, runId, 'succeeded', name);
                return true;
            } catch (error: any) {
                const diagnostic: SyncDiagnostic = {
                    code: 'SYNC_PHASE_FAILED', runId, timestamp: Date.now(), severity: 'error', phase: name,
                    message: `Sync phase '${name}' failed.`,
                    ...(Number.isInteger(error?.failedTasks) ? { failedTasks: error.failedTasks } : {})
                };
                diagnostics.push(diagnostic);
                phases.push({ name, status: 'failed', startedAt: phaseStartedAt, completedAt: Date.now(), diagnosticCode: 'SYNC_PHASE_FAILED', failedTasks: diagnostic.failedTasks });
                this.ctx.emitSyncDiagnostic(diagnostic);
                this.ctx.emitSyncProgress(stage, undefined, undefined, runId, 'failed', name);
                return false;
            }
        };

        if (this.ctx.syncWorker) {
            Logger.info('Sovereign', 'Delegating sync to background worker...');
            const result = await this.ctx.syncWorker.sync(forceSync);
            if (this.ctx.closeDatabases) {
                try {
                    await this.ctx.closeDatabases();
                } catch (err: any) {
                    Logger.warn('Sync', `Failed to close database pool after worker sync: ${err.message}`);
                }
            }
            return result;
        }

        if (!this.ctx.getRemote() || !this.ctx.getPublicRemote() || !this.ctx.getGlobalRemote()) {
            Logger.info('Sovereign', 'Remote not connected, skipping sync.');
            const diagnostic: SyncDiagnostic = { code: 'SYNC_REMOTE_UNAVAILABLE', runId, timestamp: Date.now(), severity: 'warning', phase: 'remote_preflight', message: 'Sync was skipped because one or more remote adapters are unavailable.' };
            diagnostics.push(diagnostic);
            this.ctx.emitSyncDiagnostic(diagnostic);
            phases.push({ name: 'remote_preflight', status: 'skipped', startedAt, completedAt: Date.now() });
            return finish();
        }

        if (this.ctx.isSyncing()) {
            Logger.info('Sovereign', 'Sync already in progress, skipping...');
            const diagnostic: SyncDiagnostic = { code: 'SYNC_ALREADY_RUNNING', runId, timestamp: Date.now(), severity: 'warning', phase: 'preflight', message: 'Sync was skipped because another sync is already running.' };
            diagnostics.push(diagnostic);
            this.ctx.emitSyncDiagnostic(diagnostic);
            phases.push({ name: 'preflight', status: 'skipped', startedAt, completedAt: Date.now() });
            return finish();
        }
        this.ctx.emitSyncProgress('start', undefined, undefined, runId, 'running');
        this.ctx.setSyncing(true);
        try {
            if (this.ctx.flushDatabases) {
                try {
                    await this.ctx.flushDatabases();
                } catch (err: any) {
                    Logger.warn('Sync', `Failed to flush pending database writes before sync: ${err.message}`);
                }
            }

            if (forceSync) {
                Logger.info('Sync', 'FORCE SYNC initiated. Bypassing ETag cache.');
            }

            let remoteManifest: SovereignManifest | null = null;
            const publicRemote = this.ctx.getPublicRemote();
            await phase('manifest_fetch', async () => {
                if (publicRemote && !forceSync) {
                    const result = await publicRemote.downloadFile(PATHS.MANIFEST);
                    if (result && result.data) {
                        remoteManifest = JSON.parse(new TextDecoder().decode(result.data));
                        if (remoteManifest && remoteManifest.subManifests && Object.keys(remoteManifest.subManifests).length > 0 && this.ctx.resolveFullManifest) {
                            remoteManifest = await this.ctx.resolveFullManifest(this.ctx.config.paths.userId, remoteManifest);
                        }
                        Logger.info('Sync', 'Remote manifest downloaded for diffing.');
                    }
                }
            });

            const lastSync = forceSync ? null : await this.ctx.storage.getLastSyncDate();
            const today = SovereignS3nc.getDateStr(new Date()); 
            
            let currentDate: Date;
            if (lastSync) {
                currentDate = new Date(lastSync);
            } else {
                currentDate = new Date();
            }

            const end = new Date();
            currentDate.setUTCHours(0, 0, 0, 0);
            end.setUTCHours(0, 0, 0, 0);
            
            const syncDates = new Set<string>();
            syncDates.add(today);

            let iterDate = new Date(currentDate);
            while (iterDate <= end) {
                syncDates.add(SovereignS3nc.getDateStr(iterDate));
                iterDate.setUTCDate(iterDate.getUTCDate() + 1);
            }
            
            const sortedDates = Array.from(syncDates).sort();
            const dateTasks = sortedDates.flatMap(dateStr => [
                () => this.syncDay(dateStr, 'private', undefined, this.ctx.getRemote(), remoteManifest),
                () => this.syncDay(dateStr, 'public', undefined, this.ctx.getPublicRemote(), remoteManifest)
            ]);
            await phase('own_data', async () => { await this.runBatched(dateTasks, DEFAULTS.SYNC_BATCH_SIZE); }, dateTasks.length);

            await phase('registration', async () => {
                await this.syncUserFile(remoteManifest);
                await this.ctx.ensureGlobalRegistration();
                await this.ctx.updateFollowingPublicKeys();
            });
            
            if (this.ctx.config.autoFollowDiscoveredUsers !== false) {
                await phase('discovery', async () => {
                    const userList = await this.ctx.discoverUsers();
                    if (userList) await this.ctx.autoFollowUsers(userList);
                });
            }

            await phase('followed_data', async () => { await this.syncFollowedUsers(today, forceSync); });
            await phase('groups', async () => { await this.ctx.syncGroups(today); });

            await phase('blobs', async () => {
                const localManifest = await this.ctx.generateManifest();
                const allFilePaths = new Set([
                    ...localManifest.blobs,
                    ...(remoteManifest && remoteManifest.files ? Object.keys(remoteManifest.files) : [])
                ]);

                const blobTasks = Array.from(allFilePaths).map(filePath => {
                    if (filePath.includes(PATHS.USER_PROFILE) || filePath.includes(PATHS.MANIFEST) ||
                        filePath.includes(PATHS.KEYS) || filePath.includes(PATHS.SENTINEL) ||
                        filePath.includes('.probe')) return null;
                    const parts = filePath.split('/');
                    if (parts.length === 2 && filePath.endsWith(PATHS.DB_EXT)) return null;

                    const type = filePath.startsWith(PATHS.PUBLIC_PREFIX) ? 'public' : 'private';
                    const relativePath = filePath.substring(type.length + 1);
                    return () => this.syncGenericFile(relativePath, type, remoteManifest);
                }).filter(t => t !== null) as (() => Promise<void>)[];

                this.ctx.emitSyncProgress('syncing_blobs', 0, blobTasks.length, runId, 'running', 'blobs');
                await this.runBatched(blobTasks, DEFAULTS.SYNC_BATCH_SIZE);
            });

            await phase('moderation', async () => { await this.ctx.processModerationRequests(); });
            await phase('manifest_publish', async () => { await this.ctx.syncManifest(); });
            await phase('retention', async () => { await this.applyRetentionPolicy(); });
            if (phases.some(p => p.status === 'failed')) {
                phases.push({ name: 'checkpoint', status: 'skipped', startedAt: Date.now(), completedAt: Date.now() });
            } else {
                await phase('checkpoint', async () => { await this.ctx.storage.setLastSyncDate(today); });
            }
            const result = finish();
            this.ctx.emitSyncProgress('complete', undefined, undefined, runId, result.status === 'succeeded' ? 'succeeded' : 'failed');
            return result;
        } catch (_error) {
            const diagnostic: SyncDiagnostic = {
                code: 'SYNC_PHASE_FAILED', runId, timestamp: Date.now(), severity: 'error', phase: 'orchestration',
                message: 'Sync orchestration failed before all phases completed.'
            };
            diagnostics.push(diagnostic);
            phases.push({ name: 'orchestration', status: 'failed', startedAt: Date.now(), completedAt: Date.now(), diagnosticCode: 'SYNC_PHASE_FAILED' });
            this.ctx.emitSyncDiagnostic(diagnostic);
            const result = finish();
            this.ctx.emitSyncProgress('complete', undefined, undefined, runId, 'failed');
            return result;
        } finally {
            this.ctx.setSyncing(false);
        }
    }

    /**
     * Enforces local data retention policy by pruning date-partitioned files
     * that exceed the configured age limits (maxDaysOwnData, maxDaysFollowedData).
     * Returns the count of deleted files.
     */
    public async applyRetentionPolicy(): Promise<number> {
        const policy = this.ctx.config.retentionPolicy;
        if (!policy) return 0;

        let prunedCount = 0;
        const now = new Date();

        const getCutoff = (days: number): string => {
            const d = new Date(now);
            d.setUTCDate(d.getUTCDate() - days);
            return SovereignS3nc.getDateStr(d);
        };

        const allFiles = await this.ctx.storage.listFiles('');
        const dateRegex = /\b(\d{4}-\d{2}-\d{2})\b/;

        for (const file of allFiles) {
            const match = file.match(dateRegex);
            if (!match) continue;
            const fileDate = match[1];

            if (policy.maxDaysFollowedData !== undefined && file.startsWith(PATHS.FOLLOWED_PREFIX)) {
                const cutoff = getCutoff(policy.maxDaysFollowedData);
                if (fileDate < cutoff) {
                    await this.ctx.storage.deleteFile(file);
                    prunedCount++;
                    Logger.debug('Retention', `Pruned old followed file: ${file}`);
                }
            } else if (policy.maxDaysOwnData !== undefined && !file.startsWith(PATHS.FOLLOWED_PREFIX)) {
                const cutoff = getCutoff(policy.maxDaysOwnData);
                if (fileDate < cutoff) {
                    await this.ctx.storage.deleteFile(file);
                    prunedCount++;
                    Logger.debug('Retention', `Pruned old own file: ${file}`);
                }
            }
        }

        if (prunedCount > 0) {
            Logger.info('Retention', `Applied retention policy: pruned ${prunedCount} old files.`);
        }
        return prunedCount;
    }

    public async syncDay(date: string, type: 'private' | 'public', localPublicKey?: string, remoteOverride?: IRemoteAdapter, remoteManifest?: SovereignManifest | null) {
        try {
            const remotePath = `${type}/${date}${PATHS.DB_EXT}`;
            const activeRemote = remoteOverride || this.ctx.getRemote();
            if (!activeRemote) return;
            
            let localData = await this.ctx.storage.getDailyDb(date, type);
            const currentKey = type === 'private' ? this.ctx.config.encryptionKey : undefined;
            const cachedEtag = await this.ctx.storage.getRemoteHashCache(date, type);
            const cachedSyncHash = await this.ctx.storage.getGenericRemoteHashCache(`sync_hash:${remotePath}`);

            if (!localData) {
                const hasOnRemote = remoteManifest?.files?.[remotePath] !== undefined;
                if (hasOnRemote || !remoteManifest) {
                    Logger.info('Sync', `Downloading ${remotePath}`);
                    const result = await activeRemote.downloadFile(remotePath);
                    if (result && result.data) {
                        let data = result.data;
                        let remoteHash: string | null | undefined = remoteManifest?.files?.[remotePath]?.hash;
                        if (remoteHash === undefined) {
                            remoteHash = await activeRemote.getFileHash(remotePath);
                        }
                        if (currentKey) {
                            data = await this.ctx.decrypt(data, currentKey);
                        }
                        await this.ctx.storage.saveDailyDb(date, type, data);
                        if (result.etag) await this.ctx.storage.setRemoteHashCache(date, type, result.etag);
                        if (remoteHash) await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${remotePath}`, remoteHash);
                    }
                }
            } else {
                const localHash = this.ctx.calculateHashedContent(localData, currentKey);
                
                let remoteHash: string | null | undefined = remoteManifest?.files?.[remotePath]?.hash;
                if (remoteHash === undefined) {
                    remoteHash = await activeRemote.getFileHash(remotePath);
                }
                
                if (localHash === remoteHash) {
                    if (!cachedEtag || !cachedSyncHash) {
                        const remoteEtag = await activeRemote.getFileEtag(remotePath);
                        if (remoteEtag) await this.ctx.storage.setRemoteHashCache(date, type, remoteEtag);
                        if (remoteHash) await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${remotePath}`, remoteHash);
                    }
                    return;
                }

                if (cachedSyncHash && remoteHash !== cachedSyncHash && localHash !== cachedSyncHash) {
                    Logger.warn('Sync', `Conflict detected for ${remotePath}`);
                    const result = await activeRemote.downloadFile(remotePath);
                    if (result && result.data) {
                        let remoteData = result.data;
                        if (currentKey) {
                            try {
                                remoteData = await this.ctx.decrypt(remoteData, currentKey);
                            } catch (e: any) {
                                Logger.error('Sync', `Failed to decrypt remote conflict file: ${e.message}`);
                            }
                        }
                        
                        const choice = await this.ctx.handleConflict(remotePath, localData, remoteData);
                        if (choice === 'remote') {
                            localData = remoteData;
                            await this.ctx.storage.saveDailyDb(date, type, localData);
                            if (result.etag) await this.ctx.storage.setRemoteHashCache(date, type, result.etag);
                            if (remoteHash) await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${remotePath}`, remoteHash);
                            return;
                        } else if (typeof choice === 'object' && choice.mergedData) {
                            localData = choice.mergedData;
                            await this.ctx.storage.saveDailyDb(date, type, localData);
                            // We don't have an ETag for merged data yet, so we'll upload it next
                        } else if (choice === 'abort') {
                            Logger.info('Sync', `Conflict for ${remotePath} skipped by user.`);
                            return;
                        }
                    }
                }

                Logger.info('Sync', `Uploading ${remotePath}`);
                let uploadData = localData;
                if (currentKey) {
                    uploadData = await this.ctx.encrypt(localData, currentKey);
                }
                const etag = await activeRemote.uploadFile(remotePath, uploadData, localHash);
                if (etag) await this.ctx.storage.setRemoteHashCache(date, type, etag);
                await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${remotePath}`, localHash);
            }
        } catch (e: any) {
            Logger.warn('Sync', `syncDay failed for ${date}/${type} (${e?.name || 'Error'}).`);
            throw e;
        }
    }

    public async syncFollowedUsers(today: string, forceSync: boolean = false) {
        const following = await this.ctx.storage.getFollowing();
        const usersToSync = [...following];

        if (this.ctx.config.adminPublicKey && !usersToSync.find(u => u.userId === 'admin')) {
            const startDate = new Date();
            startDate.setUTCDate(startDate.getUTCDate() - 7);
            usersToSync.push({
                userId: 'admin',
                publicKey: this.ctx.config.adminPublicKey,
                lastSync: SovereignS3nc.getDateStr(startDate)
            });
        }

        const blacklist = this.ctx.config.blacklist || [];
        const eligibleUsers = usersToSync.filter(u => !blacklist.includes(u.userId));

        // Process followed users in concurrent batches
        const BATCH_SIZE = 5;
        for (let i = 0; i < eligibleUsers.length; i += BATCH_SIZE) {
            const batch = eligibleUsers.slice(i, i + BATCH_SIZE);
            await Promise.all(batch.map(user => this.syncFollowedUser(user, today, forceSync)));
        }
    }

    public async syncFollowedUser(user: any, today: string, forceSync: boolean = false) {
        const meta = this.ctx.fetchManifestWithMeta
            ? await this.ctx.fetchManifestWithMeta(user.userId, { forceRefresh: forceSync })
            : { manifest: await this.ctx.fetchManifest(user.userId), etag: null, unchanged: false, fromCache: false };

        const manifest = meta.manifest;
        if (manifest) {
            // Check if manifest ETag is unchanged and we've already synced this exact manifest version
            const lastProcessedEtag = await this.ctx.storage.getGenericRemoteHashCache(`manifest_etag:${user.userId}`);
            if (!forceSync && meta.unchanged && meta.etag && lastProcessedEtag === meta.etag) {
                Logger.info('Sync', `Skipping unchanged manifest for ${user.userId} (ETag: ${meta.etag}, fromCache: ${meta.fromCache})`);
                await this.ctx.storage.updateFollowedUserSync(user.userId, today);
                return;
            }

            Logger.info('Sync', `Using manifest for ${user.userId}`);

            const unchangedPartitions = new Set<string>();
            if (!forceSync && manifest.subManifests) {
                for (const [key, ref] of Object.entries(manifest.subManifests)) {
                    const cacheKey = `submanifest:${user.userId}:${ref.path}`;
                    const cachedHash = await this.ctx.storage.getGenericRemoteHashCache(cacheKey);
                    if (cachedHash === ref.hash) {
                        unchangedPartitions.add(key);
                    }
                }
            }

            const isPartitionUnchanged = (dateStr: string) => {
                const year = (dateStr.length >= 4 && /^\d{4}$/.test(dateStr.substring(0, 4))) ? dateStr.substring(0, 4) : 'misc';
                return unchangedPartitions.has(year);
            };

            if (manifest.modules['core']) {
                for (const dateStr of manifest.modules['core']) {
                    if (isPartitionUnchanged(dateStr)) continue;
                    await this.pullUserDay(user.userId, dateStr, user.publicKey);
                }
            }

            for (const [moduleName, dates] of Object.entries(manifest.modules)) {
                if (moduleName === 'core') continue;
                for (const dateStr of dates) {
                    if (isPartitionUnchanged(dateStr)) continue;
                    const remotePath = this.ctx.getModulePath(moduleName, `${dateStr}.db`, 'public');
                    const localPath = this.ctx.getModulePath(moduleName, `${user.userId}/${dateStr}.db`, 'followed');
                    const changed = await this.pullUserFile(user.userId, remotePath, user.publicKey, localPath, false);
                    if (changed) await this.ctx.onModuleUpdate(moduleName, localPath);
                }
            }

            const myId = this.ctx.config.paths.userId;
            if (manifest.dms && manifest.dms[myId]) {
                for (const dateStr of manifest.dms[myId]) {
                    if (isPartitionUnchanged(dateStr)) continue;
                    for (const moduleDef of this.ctx.registeredModules) {
                        const moduleName = moduleDef.name;
                        const dmPath = this.ctx.getModulePath(moduleName, `dms/${myId}/${dateStr}.db`, 'public');
                        const localPath = this.ctx.getModulePath(moduleName, `${user.userId}/dms/${myId}/${dateStr}.db`, 'followed');
                        const changed = await this.pullUserFile(user.userId, dmPath, user.publicKey, localPath, false);
                        if (changed) await this.ctx.onModuleUpdate(moduleName, localPath);
                    }
                }
            }

            // Pull read receipt files that the followed user has written acknowledging our messages.
            // Receipt path on sender (followed user): public/modules/{module}/receipts/{myId}/{date}.db
            // Pulled to local: followed/{userId}/modules/{module}/receipts/{myId}/{date}.db
            if (manifest.receipts && manifest.receipts[myId]) {
                for (const dateStr of manifest.receipts[myId]) {
                    if (isPartitionUnchanged(dateStr)) continue;
                    for (const moduleDef of this.ctx.registeredModules) {
                        const moduleName = moduleDef.name;
                        const receiptPath = this.ctx.getModulePath(moduleName, `receipts/${myId}/${dateStr}.db`, 'public');
                        const localPath = this.ctx.getModulePath(moduleName, `${user.userId}/receipts/${myId}/${dateStr}.db`, 'followed');
                        await this.pullUserFile(user.userId, receiptPath, user.publicKey, localPath, false);
                    }
                }
            }

            if (manifest.subManifests) {
                for (const [key, ref] of Object.entries(manifest.subManifests)) {
                    if (!unchangedPartitions.has(key)) {
                        const cacheKey = `submanifest:${user.userId}:${ref.path}`;
                        await this.ctx.storage.setGenericRemoteHashCache(cacheKey, ref.hash);
                    }
                }
            }

            if (meta.etag) {
                await this.ctx.storage.setGenericRemoteHashCache(`manifest_etag:${user.userId}`, meta.etag);
            }
        } else {
            const startDate = new Date(user.lastSync);
            const endDate = new Date(today);
            
            let iter = new Date(startDate);
            iter.setUTCHours(0, 0, 0, 0);
            endDate.setUTCHours(0, 0, 0, 0);

            while (iter <= endDate) {
                const dateStr = SovereignS3nc.getDateStr(iter);
                await this.pullUserDay(user.userId, dateStr, user.publicKey);
                
                for (const moduleDef of this.ctx.registeredModules) {
                    const moduleName = moduleDef.name;
                    const remotePath = this.ctx.getModulePath(moduleName, `${dateStr}.db`, 'public');
                    const localPath = this.ctx.getModulePath(moduleName, `${user.userId}/${dateStr}.db`, 'followed');
                    await this.pullUserFile(user.userId, remotePath, user.publicKey, localPath, false);

                    const myId = this.ctx.config.paths.userId;
                    const dmPath = this.ctx.getModulePath(moduleName, `dms/${myId}/${dateStr}.db`, 'public'); 
                    const dmLocalPath = this.ctx.getModulePath(moduleName, `${user.userId}/dms/${myId}/${dateStr}.db`, 'followed');
                    await this.pullUserFile(user.userId, dmPath, user.publicKey, dmLocalPath, false);

                    // Fallback: also try pulling receipt files per date
                    const receiptPath = this.ctx.getModulePath(moduleName, `receipts/${myId}/${dateStr}.db`, 'public');
                    const receiptLocalPath = this.ctx.getModulePath(moduleName, `${user.userId}/receipts/${myId}/${dateStr}.db`, 'followed');
                    await this.pullUserFile(user.userId, receiptPath, user.publicKey, receiptLocalPath, false);
                }

                iter.setUTCDate(iter.getUTCDate() + 1);
            }
        }
        
        await this.ctx.storage.updateFollowedUserSync(user.userId, today);
    }

    public async pullUserFile(userId: string, remotePath: string, publicKey: string, localPath: string, expectEncrypted: boolean = true): Promise<boolean> {
        try {
            const userRemote = this.ctx.createRemote(userId);
            const cachedEtag = await this.ctx.storage.getGenericRemoteHashCache(`${userId}:${remotePath}`);
            
            const result = await userRemote.downloadFile(remotePath, cachedEtag || undefined);
            
            if (result && !result.notModified && result.data) {
                let data = result.data;
                try {
                    if (expectEncrypted) {
                        data = await this.ctx.decrypt(data, publicKey);
                    }
                    await this.ctx.storage.saveFile(localPath, data);
                    if (result.etag) await this.ctx.storage.setGenericRemoteHashCache(`${userId}:${remotePath}`, result.etag);
                    return true;
                } catch (e: any) {
                    Logger.warn('Sync', `Failed to process ${remotePath} from ${userId}: ${e.message}`);
                }
            } else if (!result) {
                await this.ctx.storage.deleteFile(localPath);
                await this.ctx.storage.setGenericRemoteHashCache(`${userId}:${remotePath}`, '');
                return true;
            }
        } catch (e: any) {
            Logger.warn('Sync', `pullUserFile failed for ${userId}/${remotePath}: ${e.message}`);
        }
        return false;
    }

    public async pullUserDay(userId: string, date: string, publicKey: string) {
        try {
            const userRemote = this.ctx.createRemote(userId);
            const filePath = `${PATHS.PUBLIC_PREFIX}${date}${PATHS.DB_EXT}`;
            const cachedEtag = await this.ctx.storage.getRemoteHashCache(`${userId}:${date}`, 'followed' as any);

            const result = await userRemote.downloadFile(filePath, cachedEtag || undefined);

            if (result && !result.notModified && result.data) {
                let data = result.data;
                await this.ctx.storage.saveDailyDb(`${userId}/${date}`, 'followed' as any, data);
                if (result.etag) await this.ctx.storage.setRemoteHashCache(`${userId}:${date}`, 'followed' as any, result.etag);
            } else if (!result) {
                await this.ctx.storage.deleteDailyDb(`${userId}/${date}`, 'followed' as any);
                await this.ctx.storage.setRemoteHashCache(`${userId}:${date}`, 'followed' as any, '');
            }
        } catch (e: any) {
            Logger.warn('Sync', `pullUserDay failed for ${userId}/${date}: ${e.message}`);
        }
    }

    public async syncUserFile(remoteManifest?: SovereignManifest | null) {
        try {
            const publicRemote = this.ctx.getPublicRemote();
            if (!publicRemote) return;
            const remotePath = PATHS.USER_PROFILE;
            let localData = await this.ctx.storage.getPublicUserFile();
            const key = this.ctx.config.publicEncryptionKey;
            const cachedEtag = await this.ctx.storage.getGenericRemoteHashCache(remotePath);

            const result = await publicRemote.downloadFile(remotePath, cachedEtag || undefined);
            
            if (result && !result.notModified && result.data) {
                let remoteDecrypted = result.data;
                let remoteParsed: any = null;
                
                try {
                    remoteParsed = JSON.parse(new TextDecoder().decode(result.data));
                } catch (e) {
                    try {
                        if (key) {
                            remoteDecrypted = await this.ctx.decrypt(result.data, key);
                            remoteParsed = JSON.parse(new TextDecoder().decode(remoteDecrypted));
                        }
                    } catch (de) {
                        Logger.warn('Sync', 'Failed to decrypt/parse remote user.json. Overwriting with local if possible.');
                    }
                }

                if (remoteParsed) {
                    let shouldKeepLocal = false;
                    if (localData) {
                        try {
                            const localObj = JSON.parse(new TextDecoder().decode(localData));
                            if (localObj.updatedAt && remoteParsed.updatedAt && localObj.updatedAt > remoteParsed.updatedAt) {
                                shouldKeepLocal = true;
                            }
                        } catch (e: any) {
                            Logger.warn('Sync', `Failed to compare user.json timestamps: ${e.message}`);
                        }
                    }

                    if (!shouldKeepLocal) {
                        localData = remoteDecrypted;
                        await this.ctx.storage.savePublicUserFile(localData);
                        if (result.etag) await this.ctx.storage.setGenericRemoteHashCache(remotePath, result.etag);
                    }
                } else if (localData) {
                    Logger.warn('Sync', 'Remote user.json is invalid or unparseable. Preserving local user.json.');
                }
            }

            if (localData) {
                let remoteHash: string | null | undefined = remoteManifest?.files?.[remotePath]?.hash;
                if (remoteHash === undefined) {
                    remoteHash = await publicRemote.getFileHash(remotePath);
                }
                const localHash = this.ctx.calculateHashedContent(localData, key);
                
                if (localHash !== remoteHash) {
                    Logger.info('Sync', `Uploading ${remotePath}`);
                    let uploadData = localData;
                    if (key) uploadData = await this.ctx.encrypt(localData, key);
                    const etag = await publicRemote.uploadFile(remotePath, uploadData, localHash);
                    if (etag) await this.ctx.storage.setGenericRemoteHashCache(remotePath, etag);
                } else if (!cachedEtag && remoteHash) {
                    const remoteEtag = await publicRemote.getFileEtag(remotePath);
                    if (remoteEtag) await this.ctx.storage.setGenericRemoteHashCache(remotePath, remoteEtag);
                }
            }
        } catch (e: any) {
            Logger.warn('Sync', `syncUserFile failed: ${e.message}`);
        }
    }

    public async syncGenericFile(relativePath: string, type: 'private' | 'public', remoteManifest?: SovereignManifest | null) {
        try {
            const activeRemote = type === 'public' ? this.ctx.getPublicRemote() : this.ctx.getRemote();
            if (!activeRemote) return;
            const key = type === 'private' ? this.ctx.config.encryptionKey : undefined;
            
            const fullPath = relativePath.startsWith(`${type}/`) ? relativePath : `${type}/${relativePath}`;
            const s3Path = fullPath;

            let localData = await this.ctx.storage.getFile(fullPath);
            const cachedSyncHash = await this.ctx.storage.getGenericRemoteHashCache(`sync_hash:${fullPath}`);
            
            let remoteHash: string | null | undefined = remoteManifest?.files?.[fullPath]?.hash;
            if (remoteHash === undefined) {
                remoteHash = await activeRemote.getFileHash(s3Path);
            }

            if (!localData) {
                if (remoteHash) {
                    Logger.info('Sync', `Downloading generic file: ${fullPath}`);
                    const result = await activeRemote.downloadFile(s3Path);
                    if (result && result.data) {
                        let data = result.data;
                        if (key) data = await this.ctx.decrypt(data, key);
                        await this.ctx.storage.saveFile(fullPath, data);
                        if (result.etag) await this.ctx.storage.setGenericRemoteHashCache(fullPath, result.etag);
                        if (remoteHash) await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${fullPath}`, remoteHash);
                    }
                }
            } else {
                const localHash = this.ctx.calculateHashedContent(localData, key);

                if (localHash === remoteHash) {
                    const cachedEtag = await this.ctx.storage.getGenericRemoteHashCache(fullPath);
                    if (!cachedEtag || !cachedSyncHash) {
                        const remoteEtag = await activeRemote.getFileEtag(s3Path);
                        if (remoteEtag) await this.ctx.storage.setGenericRemoteHashCache(fullPath, remoteEtag);
                        if (remoteHash) await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${fullPath}`, remoteHash);
                    }
                    return;
                }

                if (cachedSyncHash && remoteHash !== cachedSyncHash && localHash !== cachedSyncHash) {
                    Logger.warn('Sync', `Conflict detected for ${fullPath}`);
                    const result = await activeRemote.downloadFile(s3Path);
                    if (result && result.data) {
                        let remoteData = result.data;
                        if (key) {
                            try {
                                remoteData = await this.ctx.decrypt(remoteData, key);
                            } catch (e: any) {
                                Logger.error('Sync', `Failed to decrypt remote conflict file: ${e.message}`);
                            }
                        }
                        
                        const choice = await this.ctx.handleConflict(fullPath, localData, remoteData);
                        if (choice === 'remote') {
                            localData = remoteData;
                            await this.ctx.storage.saveFile(fullPath, localData);
                            if (result.etag) await this.ctx.storage.setGenericRemoteHashCache(fullPath, result.etag);
                            if (remoteHash) await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${fullPath}`, remoteHash);
                            return;
                        } else if (typeof choice === 'object' && choice.mergedData) {
                            localData = choice.mergedData;
                            await this.ctx.storage.saveFile(fullPath, localData);
                            // Upload merged version next
                        } else if (choice === 'abort') {
                            Logger.info('Sync', `Conflict for ${fullPath} skipped by user.`);
                            return;
                        }
                    }
                }

                Logger.info('Sync', `Uploading generic file: ${fullPath}`);
                let uploadData = localData;
                if (key) uploadData = await this.ctx.encrypt(localData, key);
                const etag = await activeRemote.uploadFile(s3Path, uploadData, localHash);
                if (etag) await this.ctx.storage.setGenericRemoteHashCache(fullPath, etag);
                await this.ctx.storage.setGenericRemoteHashCache(`sync_hash:${fullPath}`, localHash);
            }
        } catch (e: any) {
            Logger.warn('Sync', `syncGenericFile failed for ${relativePath} (${e?.name || 'Error'}).`);
            throw e;
        }
    }

    private async runBatched<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
        const results: T[] = new Array(tasks.length);
        const errors: unknown[] = [];
        let currentIndex = 0;
        const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
            while (currentIndex < tasks.length) {
                const index = currentIndex++;
                try {
                    results[index] = await tasks[index]();
                } catch (error) {
                    errors.push(error);
                }
            }
        });
        await Promise.all(workers);
        if (errors.length > 0) {
            const error = new Error(`${errors.length} sync task(s) failed`);
            (error as any).failedTasks = errors.length;
            throw error;
        }
        return results;
    }

    /**
     * Runs compaction maintenance on registered modules for specified dates.
     */
    async compactDatabases(dates?: string[], force: boolean = false): Promise<void> {
        const today = SovereignS3nc.getDateStr(new Date());
        const targetDates = dates && dates.length > 0 ? dates : [today];
        const instances = this.ctx.getModuleInstances ? this.ctx.getModuleInstances() : [];
        for (const mod of instances) {
            if (typeof (mod as any).compactDatabase === 'function') {
                for (const d of targetDates) {
                    try {
                        if (mod.MODULE_NAME === 'feed') {
                            await (mod as any).compactDatabase(d, true, force);
                        } else {
                            await (mod as any).compactDatabase(d, force);
                        }
                    } catch (e) {
                        Logger.warn('Sync', `Compaction failed for module on date ${d}: ${e}`);
                    }
                }
            }
        }
    }
}
